import React from "react";
import { Table, Container, Row } from "reactstrap";
import DeleteButton from "../Buttons/DeleteButton";
import { useEffect, useState, useContext } from "react";
import DeleteDeviceModal from "../Shared/ViewModels/DeleteDeviceModalViewModel";
import { updateDevice, createDevice, deleteDevice, getFullSubOrganizations, getSubscription, getDevice, getDevicePrinters } from "../../utilities/orgPanelApiCalls";
import { errorToast, successToast, warningToast } from '../../constants/toast';
import HTTPStatus from "../../components/Enums/HTTPStatus";
import ManageDeviceModal from "../../components/Shared/ManageDeviceModal";
import { RoundedButton, ButtonColor, ButtonCorner } from "../../components/Buttons/RoundedButton";
import SubscriptionDisplay from "../../components/SubscriptionDisplay/SubscriptionDisplay";
import { useStore, setSubscription } from "../../utilities/appStore";

const DeviceGrid = ({ data, setIsLoading, isLoading, refreshDevices, toastCallback }) => {

    const [loader] = useState(0);
    const [manageData, setManageData] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [locationData, setLocationData] = useState(null);
    const [printerData, setPrinterData] = useState([]);
    const [showManageModal, setShowManageModal] = useState(false);
    const [modalIsLoading, setModalIsLoading] = useState(false);


    const subscriptionData = useStore((state) => state.Subscription);
    const currentOrganization = useStore((state) => state.CurrentOrganizationId);

    const toggleDeleteModal = (item) => {
        setManageData(item);
        setShowDeleteModal(!showDeleteModal);
    }

    const makeToast = toastCallback;

    useEffect(() => {
        async function load() {
            var subOrgPromise = getFullSubOrganizations(currentOrganization);
            var subscriptionPromise = getSubscription(currentOrganization);


            var subOrgResponse = await subOrgPromise;
            if (subOrgResponse.status > 299)
                console.log("Failed to load sub orgs!");

            var subOrgData = [...subOrgResponse.data]

            var locations = [];
            for (var i = 0; i < subOrgData.length; i++) {
                //Filter to only populate with organization type locations
                if (subOrgData[i].accountTypeId === 5) {
                    locations.push({
                        label: subOrgData[i].name,
                        value: subOrgData[i].accountID.toString()
                    });
                }
            }
            var subscriptionResult = await subscriptionPromise;
            if (subscriptionResult.status === HTTPStatus.NotFound) {
                setSubscription(false);
                //No active subscrition
            }
            else if (subscriptionResult.status > 299)
                console.log("Failed to load subscription data");
            else {
                setSubscription(subscriptionResult.data);

            }
            setLocationData(locations);
        }
        load();

    }, [loader, currentOrganization]);

    const handleDelete = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        var response = await deleteDevice(currentOrganization, e.target.accountId?.value, subscriptionData.subscriptionId);

        if (response === 403 || response === 401) {
            makeToast("Authorization error deleting device: " + response.message, errorToast)
            toggleDeleteModal();
            setIsLoading(false);
        }
        else if (response > 299) {
            makeToast("Error deleting device: " + response.message, errorToast)
            toggleDeleteModal();
            setIsLoading(false);
        }
        else {
            makeToast("Device Deleted", successToast)
            toggleDeleteModal();
            refreshDevices();
            setIsLoading(false);
        }
    }

    const handleModify = async (e) => {
        e.preventDefault();

        var model = {
            name: e.target.name.value,
            //seatDetails: e.target.seatDetails.value,
            description: e.target.description.value,
            location: e.target.location.value,
            printerId: (e.target.printer) ? parseInt(e.target.printer.value) : manageData.printerId, //Disabled controls are not included in submit info
            isActive: true
        }

        var isValid = true;

        if (model.name === "" || model.name === null) {
            makeToast("Device name is a required field.", warningToast);
            isValid = false;
        }

        if (model.location === "" || model.location === null) {
            makeToast("Location is a required field.", warningToast);
            isValid = false;
        }

        if (model.printerId === "" || model.printerId === null) {
            makeToast("Printer is a required field.", warningToast);
            isValid = false;
        }

        if (isValid) {
            if (e.target.accountId?.value !== '' && e.target.uDID.value !== '') {

                model.accountId = e.target.accountId?.value;
                model.uDID = e.target.uDID.value;
                model.serialNumber = e.target.serialNumber.value;
                model.deviceType = parseInt(e.target.deviceType.value);
                setModalIsLoading(true);
                var response = await updateDevice(currentOrganization, model);

                if (response === 403 || response === 401) {
                    makeToast("Authorization error updating device: " + response.message, errorToast);
                    setModalIsLoading(false);
                }
                else if (response > 299) {
                    makeToast("Error updating device: " + response.message, errorToast);
                    setModalIsLoading(false);
                }
                else {
                    setShowManageModal(false);
                    refreshDevices();
                    makeToast("Device Updated", successToast);
                    setModalIsLoading(false);
                }
            }
            else {

                setModalIsLoading(true);
                var deviceResponse = await createDevice(model.location, model, subscriptionData.subscriptionId, currentOrganization);

                if (deviceResponse === 403 || deviceResponse === 401) {
                    setModalIsLoading(false);
                    makeToast("Authorization error creating device: " + deviceResponse.message, errorToast);
                }
                else if (deviceResponse > 299) {
                    setModalIsLoading(false);
                    makeToast("Error creating device: " + deviceResponse.message, errorToast);
                }
                else {
                    setShowManageModal(false);
                    refreshDevices();
                    makeToast("Device Created", successToast);
                    setModalIsLoading(false);
                }
            }
        }
    }

    const toggleManageModal = async (item) => {

        setShowManageModal(true);
        setModalIsLoading(true);

        const [deviceResult, printerResult] = await Promise.all([
            getDevice(item.accountId),
            getDevicePrinters()
        ]);


        item.printerId = deviceResult.data.printerId;

        setManageData(item);
        setPrinterData(printerResult?.data);
        setModalIsLoading(false);
    }

    const OrgSearchHeader = ({ type }) => {
        return (
            <tr className="d-flex">
                <th className="col-2">DEVICE NAME</th>
                <th className="col-2">DEVICE DESCRIPTION</th>
                <th className="col-2">LOCATION NAME</th>
                <th className="col-1">UDID</th>
                <th className="col-2">SERIAL NUMBER</th>
                <th className="col-1">LICENSE</th>
                <th className="col-1">ACTIONS</th>
                <th className="col-1"></th>
            </tr>
        )
    };

    const OrgSearchEmptyHeader = () => {
        return (
            <tr>
                <th></th>
            </tr>
        )
    };

    const OrgSearchEmptyResultsRow = () => {
        return (
            <tr>
                <td><h4 className="text-center">No Results Found</h4></td>
            </tr>
        )
    };

    const OrgSearchRow = ({ data }) => {

        return (
            <tr className="d-flex overflow-ellipsis">
                <td className="col-2 overflow-ellipsis">{data.deviceName}</td>
                <td className="col-2 overflow-ellipsis">{data.description}</td>
                <td className="col-2 overflow-ellipsis">{data.locationName}</td>
                <td className="col-1 overflow-ellipsis">{data.udid}</td>
                <td className="col-2 overflow-ellipsis">{data.serialNumber}</td>
                <td className="col-1"><SubscriptionDisplay status={data?.subscriptionStatus} type={data?.deviceType} /></td>

                <td className="col-1">
                    <RoundedButton
                        id="EditDeviceBtn"
                        hoverColor={ButtonColor.Red}
                        hoverTextColor={ButtonColor.White}
                        hoverOutlineColor={ButtonColor.Red}
                        textColor={ButtonColor.Grey}
                        buttonColor={ButtonColor.White}
                        outlineColor={ButtonColor.Grey}
                        cornerRadius={ButtonCorner.SevenPX}
                        clickHandler={() => { toggleManageModal(data) }}
                        text="Edit" />
                </td>
                <td className="col-1"><DeleteButton id="DeleteDeviceBtn" submitHandler={() => { toggleDeleteModal(data) }}></DeleteButton></td>
            </tr>
        )
    }

    return (
        <>
            <Container fluid>
                <Row className="table-responsive">
                    <Table className="table-sm table-striped">
                        <thead className="search-thead">
                            {(data === null || data.length === 0) && <OrgSearchEmptyHeader />}
                            {data !== null && data.length > 0 && <OrgSearchHeader />}
                        </thead>
                        <tbody className="search-tbody">
                            {(data === null || data.length === 0) && <OrgSearchEmptyResultsRow />}
                            {data?.map((row) => {
                                return <OrgSearchRow key={row.accountId} data={row} />
                            })}
                        </tbody>
                    </Table>
                </Row>
            </Container>

            {showManageModal &&
                <ManageDeviceModal
                isLoading={modalIsLoading}
                isOpen={showManageModal}
                submitHandler={handleModify}
                handleClose={() => setShowManageModal(false)}
                manageData={manageData}
                locationData={locationData}
                printerData={printerData}
                editDevice={true}
                refreshDevices={refreshDevices}
                deviceData={data}
                toastCallback={makeToast}
                />
            }
            {showDeleteModal &&
                <DeleteDeviceModal
                    deviceData={manageData}
                    pageToggle={toggleDeleteModal}
                    toastCallback={makeToast}
                    completeCallback={refreshDevices}
                    subscriptionId={subscriptionData.subscriptionId}
                />
            }

        </>
    )
};



export default DeviceGrid;